import React, { createContext, useContext, useReducer } from 'react'
import PropTypes from 'prop-types'

export const ChainContext = createContext()

export const ChainProvider = ({ reducer, initialState, children }) => (
  <ChainContext.Provider value={useReducer(reducer, initialState)}>
    {children}
  </ChainContext.Provider>
)

ChainProvider.propTypes = {
  reducer: PropTypes.func.isRequired,
  initialState: PropTypes.object.isRequired,
  children: PropTypes.node.isRequired,
}

export const useChain = () => useContext(ChainContext)

// --- Mapping helpers ---

export function getByPath(obj, path) {
  return path.split('.').reduce((o, k) => (o != null ? o[k] : undefined), obj)
}

export function setByPath(obj, path, value) {
  const keys = path.split('.')
  let current = obj
  for (let i = 0; i < keys.length - 1; i++) {
    if (current[keys[i]] == null) current[keys[i]] = {}
    current = current[keys[i]]
  }
  current[keys[keys.length - 1]] = value
}

export function extractOutputVars(outputState, outputMap) {
  const vars = {}
  if (!outputState || !outputState.data || !Array.isArray(outputMap)) return vars
  outputMap.forEach(m => {
    const val = getByPath(outputState.data, m.from)
    if (val !== undefined) vars[m.name] = String(val)
  })
  return vars
}

export function buildMappedInputState(accumulatedVars, inputMap) {
  if (!inputMap || !inputMap.type || !Array.isArray(inputMap.mappings) || inputMap.mappings.length === 0) return null
  const inputState = { type: inputMap.type, data: {} }
  inputMap.mappings.forEach(m => {
    const val = accumulatedVars[m.name] !== undefined
      ? accumulatedVars[m.name]
      : (m.default !== undefined ? m.default : undefined)
    if (val !== undefined) setByPath(inputState.data, m.to, val)
  })
  return inputState
}

// --- State builder ---

export function buildChainInitialState(config) {
  const simulations = (config.simulations || []).map((sim, index) => ({
    ...sim,
    status: index === 0 ? 'active' : 'pending',
    inputState: null,
    outputState: null,
  }))

  return {
    title: config.title || '',
    description: config.description || '',
    simulations: simulations,
    activeIndex: 0,
    accumulatedState: { raw: null, vars: {} },
    chainComplete: false,
    reviewIndex: null,
    activityPk: config.activityPk || '',
    coursenum: config.coursenum || '',
    completionUrl: config.completionUrl || '',
    completionButtonText: config.completionButtonText || '',
  }
}

// --- Reducer ---

export function chainReducer(state, action) {
  switch (action.type) {
    case 'COMPLETE_SIMULATION': {
      const { index, outputState, stateSnapshot } = action.payload
      if (index !== state.activeIndex) return state

      // Prefer a snapshot passed with the action over the one synced via
      // UPDATE_ACTIVE_STATE.  The manual Continue/Finish path passes the live
      // snapshot directly so completedTasks reflect real per-task status even
      // if the click races ahead of the latest onStateChange.
      const effectiveSnapshot = stateSnapshot || state.simulations[index].stateSnapshot

      const simulations = state.simulations.map((sim, i) => {
        if (i === index) {
          return { ...sim, status: 'completed', outputState: outputState, completedTasks: effectiveSnapshot ? effectiveSnapshot.tasks : [], stateSnapshot: effectiveSnapshot }
        }
        return sim
      })

      const completedSim = state.simulations[index]
      const prevAccumulated = state.accumulatedState || { raw: null, vars: {} }

      // Resolve raw state based on statePolicy
      let newRaw
      if (completedSim.statePolicy === 'passthrough') {
        newRaw = prevAccumulated.raw
      } else {
        newRaw = outputState
      }

      // Extract named vars if outputMap is defined
      let newVars = { ...prevAccumulated.vars }
      if (completedSim.outputMap) {
        const extracted = extractOutputVars(outputState, completedSim.outputMap)
        Object.assign(newVars, extracted)
      }

      const newAccumulatedState = { raw: newRaw, vars: newVars }

      const nextIndex = index + 1
      const chainComplete = nextIndex >= simulations.length

      // Activate the next simulation with mapped or raw input state
      if (!chainComplete) {
        const nextSim = simulations[nextIndex]
        let nextInputState
        if (nextSim.inputMap) {
          nextInputState = buildMappedInputState(newVars, nextSim.inputMap)
        } else {
          nextInputState = newRaw
        }

        simulations[nextIndex] = {
          ...nextSim,
          status: 'active',
          inputState: nextInputState,
        }
      }

      return {
        ...state,
        simulations,
        activeIndex: chainComplete ? state.activeIndex : nextIndex,
        accumulatedState: newAccumulatedState,
        chainComplete,
      }
    }

    case 'UPDATE_ACTIVE_STATE': {
      const { simState, forIndex } = action.payload

      // Reject stale updates from a previous simulation whose effects fired
      // after the chain already advanced (e.g. during autoAdvance).
      if (forIndex !== undefined && forIndex !== state.activeIndex) return state

      const simulations = state.simulations.map((sim, i) =>
        i === state.activeIndex ? { ...sim, stateSnapshot: simState } : sim
      )
      const newState = { ...state, simulations }

      // Auto-advance: when the active sim's tasks are all complete and the
      // step has autoAdvance, immediately complete the simulation without
      // waiting for the user to click Continue.  This is handled here in the
      // reducer (rather than in component effects) so there are no stale
      // closures or cross-component timing issues.
      const activeSim = simulations[state.activeIndex]
      if (simState && simState.allComplete && activeSim && activeSim.autoAdvance) {
        const outputState = simState._pluginOutput || null
        return chainReducer(newState, {
          type: 'COMPLETE_SIMULATION',
          payload: { index: state.activeIndex, outputState },
        })
      }

      return newState
    }

    case 'RESTORE_CHAIN': {
      // Rebuild chain state from a saved snapshot
      return { ...state, ...action.payload }
    }

    case 'RESET_CHAIN': {
      const simulations = state.simulations.map((sim, index) => ({
        ...sim,
        status: index === 0 ? 'active' : 'pending',
        inputState: null,
        outputState: null,
        stateSnapshot: null,
      }))

      return {
        ...state,
        simulations,
        activeIndex: 0,
        accumulatedState: { raw: null, vars: {} },
        chainComplete: false,
        reviewIndex: null,
      }
    }

    case 'SET_REVIEW_INDEX': {
      return { ...state, reviewIndex: action.payload }
    }

    default:
      return state
  }
}
